import React, { useState, useEffect } from 'react';
import { Landmark, Loader2, Save, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const FIELDS = [
  ['method_label', 'Payment method title'], ['reference_prefix', 'Reference prefix'],
  ['beneficiary_name', 'Beneficiary name (official)'], ['beneficiary_name_en', 'Beneficiary name (English)'],
  ['account_number', 'Account number'], ['account_currency', 'Account currency'],
  ['bank_name', 'Bank name (official)'], ['bank_name_en', 'Bank name (English)'],
  ['swift_code', 'SWIFT / CIPS code'], ['bank_address', 'Bank address'],
];

export default function BankDetailsEditor({ token }) {
  const [open, setOpen] = useState(false);
  const [bank, setBank] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || bank) return;
    fetch(`${API}/api/payments/bank-details`).then((r) => r.json()).then((d) => d.success && setBank(d.data)).catch(() => toast.error('Could not load bank details'));
  }, [open, bank]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/payments/admin/bank-details`, { method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(bank) });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Failed to save');
      setBank(d.data);
      toast.success('Bank details updated — clients see them immediately');
    } catch (err) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-6" data-testid="bank-details-editor">
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center justify-between px-5 py-3.5 text-left" data-testid="bank-details-toggle">
        <span className="flex items-center gap-2 font-bold text-gray-900"><Landmark className="w-5 h-5 text-blue-700" /> Stanbic CIPS Bank Details <span className="text-xs font-normal text-gray-500 hidden sm:inline">— shown to clients at checkout</span></span>
        {open ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
      </button>
      {open && (
        <div className="px-5 pb-5 border-t border-gray-100 pt-4">
          {!bank ? <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-blue-700" /></div> : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {FIELDS.map(([k, label]) => (
                  <label key={k} className="block">
                    <span className="text-xs font-semibold text-gray-600">{label}</span>
                    <input value={bank[k] || ''} onChange={(e) => setBank({ ...bank, [k]: e.target.value })}
                      className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:border-blue-500 focus:outline-none" data-testid={`bank-input-${k}`} />
                  </label>
                ))}
              </div>
              <Button onClick={save} disabled={saving} className="mt-4 bg-blue-700 hover:bg-blue-800 text-white font-bold rounded-xl" data-testid="bank-details-save">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />} Save bank details
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
